import { useState } from 'react'

import { Edit02Icon, Upload04Icon } from '@hugeicons/react'
import { useController, useFormContext } from 'react-hook-form'

import { AppIcon } from '@/shared/ui/icons'
import { Button } from '@/shared/ui/Button'
import { textareaVariants } from '@/shared/ui/Form/control-styles'
import { cn } from '@/lib/utils'

type MissionTextEvidenceControlsProps = {
  isSubmitting: boolean
  isReadonly: boolean
  isCapacityFull: boolean
  submitLabel: string
}

export const MissionTextEvidenceControls = ({
  isSubmitting,
  isReadonly,
  isCapacityFull,
  submitLabel
}: MissionTextEvidenceControlsProps) => {
  const [isConfirmed, setIsConfirmed] = useState(false)
  const { control } = useFormContext()
  const {
    field: { value, onChange, onBlur, ref }
  } = useController({
    name: 'content',
    control,
    rules: { required: true, minLength: 3 }
  })

  const textValue = String(value ?? '').trim()
  const textIsValid = textValue.length >= 3
  const isBusy = isSubmitting
  const isDisabled = isBusy || isCapacityFull || isReadonly

  const handleConfirm = () => {
    if (!textIsValid) return
    setIsConfirmed(true)
  }

  const handleEdit = () => {
    setIsConfirmed(false)
  }

  return (
    <div className="flex flex-col gap-3">
      {isConfirmed ? (
        <div className="flex flex-col gap-2">
          <span className="text-caps font-bold uppercase tracking-wide text-gray-700">
            Resposta confirmada
          </span>
          <p className="m-0 whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
            {textValue}
          </p>
          {!isDisabled && (
            <button
              type="button"
              onClick={handleEdit}
              className="self-start border-0 bg-transparent p-0 text-xs font-semibold text-gray-700 underline"
            >
              Editar resposta
            </button>
          )}
        </div>
      ) : (
        <>
          <label
            htmlFor="mission-evidence-text"
            className="flex items-center gap-2 text-sm font-semibold text-gray-900"
          >
            <Edit02Icon size={18} variant="duotone" className="text-primary" aria-hidden />
            Escreva sua evidência
          </label>
          <textarea
            ref={ref}
            id="mission-evidence-text"
            value={value ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            disabled={isDisabled}
            rows={5}
            placeholder="Descreva o que comprova a conclusão desta missão..."
            aria-label="Texto da evidência"
            className={cn(textareaVariants(), 'min-h-36 text-base')}
          />
        </>
      )}

      <Button
        type={isConfirmed ? 'submit' : 'button'}
        variant="primary"
        size="md"
        showTrailingIcon={false}
        className="w-full justify-center"
        disabled={isDisabled || (!isConfirmed && !textIsValid)}
        onClick={isConfirmed ? undefined : handleConfirm}
        leadingIcon={
          isBusy ? (
            <AppIcon name="spinner-third" />
          ) : isConfirmed ? (
            <Upload04Icon size={16} variant="stroke" />
          ) : (
            <Edit02Icon size={16} variant="stroke" />
          )
        }
      >
        {isSubmitting
          ? 'Enviando...'
          : isConfirmed
            ? submitLabel
            : 'Confirmar resposta'}
      </Button>
    </div>
  )
}
